
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { useAuthProfile } from "@/hooks/profile/use-auth-profile";
import { Loader2 } from "lucide-react";
import { UserProfile, UserRole } from "@/types/database.types";
import { toast } from "sonner";
import { getSupabaseUrl } from "@/utils/supabaseUrl";

interface AuthContextType {
  session: Session | null;
  user: UserProfile | null;
  loading: boolean;
  loadingAuth: boolean;
  loadingProfile: boolean;
  isAuthenticated: boolean;
  userRole: UserRole | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  clearAuthState: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// How long to wait for the initial session lookup before deciding the backend
// is unreachable. Generous enough for a slow connection, short enough that a
// dead/misconfigured backend surfaces quickly instead of hanging forever.
const AUTH_INIT_TIMEOUT_MS = 10_000;

/** Reject if `promise` hasn't settled within `ms`. */
function withTimeout<T>(promise: PromiseLike<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`Auth init timed out after ${ms}ms`)),
      ms
    );
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loadingAuth, setLoadingAuth] = useState<boolean>(true);
  // True only when the initial auth bootstrap failed at the network level
  // (unreachable/misconfigured backend), so we can show a real error.
  const [authInitFailed, setAuthInitFailed] = useState<boolean>(false);
  
  // Track which user ID we last handled to avoid re-processing same user
  const lastHandledUserIdRef = useRef<string | null>(null);
  // Throttle TOKEN_REFRESHED noise: if refreshes flood, log a warning instead
  // of cascading re-renders.
  const refreshTimestampsRef = useRef<number[]>([]);
  // True only while the user explicitly initiated sign out. Any SIGNED_OUT
  // event emitted outside this flag (e.g. caused by a 429 refresh storm) is
  // treated as transient and ignored so the user is not auto-logged-out.
  const userInitiatedSignOutRef = useRef<boolean>(false);

  // Derive session user info for profile hook
  const sessionUserId = session?.user?.id ?? null;
  const sessionUserEmail = session?.user?.email ?? null;

  // Pass session user info directly to profile hook — single source of truth
  const { profile, isLoading: loadingProfile, isError, refetchProfile } = useAuthProfile(sessionUserId, sessionUserEmail);

  // Initialize auth state
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let mounted = true;

    const init = async () => {
      try {
        // 1. Set up listener FIRST (before getSession) per Supabase best practice
        const { data } = supabase.auth.onAuthStateChange((event, newSession) => {
          if (!mounted) return;

          if (event === 'TOKEN_REFRESHED') {
            const now = Date.now();
            const recent = refreshTimestampsRef.current.filter(t => now - t < 10_000);
            recent.push(now);
            refreshTimestampsRef.current = recent;
            if (recent.length > 5) {
              if (recent.length === 6) {
                console.warn(
                  `Auth: ${recent.length} TOKEN_REFRESHED events in 10s — pausing auto-refresh for 60s`
                );
                // Belt-and-braces: hard-stop auto-refresh briefly so we
                // cannot keep hammering /token if leader election failed.
                try {
                  supabase.auth.stopAutoRefresh();
                  setTimeout(() => {
                    supabase.auth.startAutoRefresh().catch(() => {});
                  }, 60_000);
                } catch {}
              }
              return;
            }
            setSession(newSession);
            return;
          }

          console.log("Auth event:", event, "user:", newSession?.user?.id?.slice(0, 8) ?? "none");

          // State-sync only: just update session state
          // Profile loading is driven reactively by sessionUserId change
          if (event === 'SIGNED_OUT') {
            // Only honor SIGNED_OUT when the user explicitly initiated it.
            // Otherwise treat as a transient client event (e.g. 429 refresh
            // storm forcing the client to drop the session) — leave local
            // state intact so the next successful refresh recovers silently.
            if (!userInitiatedSignOutRef.current) {
              console.warn('Auth: ignoring non-user-initiated SIGNED_OUT (transient — likely refresh rate limit)');
              return;
            }
            userInitiatedSignOutRef.current = false;
            setSession(null);
            lastHandledUserIdRef.current = null;
            return;
          }

          // For all other events, sync session
          setSession(newSession);
        });

        unsubscribe = data.subscription.unsubscribe;

        // 2. Then get existing session.
        // Bounded by a timeout: if the backend is unreachable, supabase-js can
        // retry without ever settling, which would leave `loadingAuth` true
        // forever and render a blank page. Always settle so we can show a real
        // error instead of an infinite spinner.
        const { data: sessionData, error } = await withTimeout(
          supabase.auth.getSession(),
          AUTH_INIT_TIMEOUT_MS
        );
        if (error) {
          console.error("Error fetching session:", error);
        } else if (mounted) {
          setSession(sessionData.session);
        }
      } catch (error) {
        // Network-level failure or timeout (a returned auth `error` above is
        // handled separately and must not block the app).
        console.error("Error initializing auth:", error);
        if (mounted) setAuthInitFailed(true);
      } finally {
        if (mounted) setLoadingAuth(false);
      }
    };

    init();

    // Cross-tab session sync: when the leader tab refreshes the session,
    // the rotated token is written to localStorage. Follower tabs pick it
    // up here without making any /token network call themselves.
    const STORAGE_KEY = 'sb-ladquxscytpamcpyyayc-auth-token';
    const onStorage = async (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || !mounted) return;
      try {
        const { data } = await supabase.auth.getSession();
        setSession(data.session);
      } catch (err) {
        console.warn('Cross-tab session sync failed:', err);
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      mounted = false;
      window.removeEventListener('storage', onStorage);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // When session user ID changes, trigger a single guarded profile refetch
  useEffect(() => {
    if (sessionUserId && sessionUserId !== lastHandledUserIdRef.current) {
      lastHandledUserIdRef.current = sessionUserId;
      // Profile hook will automatically re-query because sessionUserId is in its queryKey
      // But we also explicitly refetch to handle the case where queryKey hasn't changed yet
      refetchProfile();
    }
    if (!sessionUserId) {
      lastHandledUserIdRef.current = null;
    }
  }, [sessionUserId, refetchProfile]);

  // Show a non-fatal toast if profile load fails (but do NOT sign out)
  useEffect(() => {
    if (isError && session) {
      toast.error("There was an error loading your profile. Some features may be limited.");
    }
  }, [isError, session]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast.error(`Sign in failed: ${error.message}`);
      throw error;
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) {
      toast.error(`Sign up failed: ${error.message}`);
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    userInitiatedSignOutRef.current = true;
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) {
      userInitiatedSignOutRef.current = false;
      toast.error(`Sign out failed: ${error.message}`);
      throw error;
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      toast.error(`Password reset failed: ${error.message}`);
      throw error;
    }
  }, []);

  const clearAuthState = useCallback(() => {
    setSession(null);
  }, []);

  const userRole = profile?.role || null;
  const isAuthenticated = !!session;

  // Auto sign-out inactive users — only when profile is confirmed for THIS session user
  useEffect(() => {
    if (session && profile && profile.status === "inactive" && profile.id === session.user.id) {
      console.warn("User profile is inactive; signing out.");
      toast.error("Your account has been deactivated. Please contact support.");
      userInitiatedSignOutRef.current = true;
      supabase.auth.signOut({ scope: 'local' }).then(() => {
        setSession(null);
      });
    }
  }, [session, profile]);

  // Initial auth bootstrap failed at the network level — the backend is
  // unreachable or misconfigured. Show the actual problem rather than sitting
  // on a spinner (which previously presented as a blank white page).
  if (authInitFailed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-lg w-full rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">
            Can’t reach the backend
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            The app couldn’t connect to its Supabase backend, so it can’t sign
            you in. This is usually a network problem or a misconfigured
            environment URL.
          </p>

          <div className="mt-4 rounded-lg bg-slate-900 p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">
              Configured backend
            </div>
            <code className="mt-1 block break-all text-[11px] text-slate-200">
              {getSupabaseUrl()}
            </code>
          </div>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex h-9 items-center rounded-md bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show loading screen only during initial auth bootstrap
  // Once auth is resolved, don't block on profile — show app with degraded mode if needed
  if (loadingAuth) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user: profile || null,
        loading: loadingAuth || loadingProfile,
        loadingAuth,
        loadingProfile,
        isAuthenticated,
        userRole,
        signIn,
        signUp,
        signOut,
        resetPassword,
        clearAuthState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
